// Public logger API.
//
// Pipeline (per call): user context → merge with logContext snapshot +
// correlationScope → sanitize → transport.shipBatch.
//
// Never writes to console; transport handles all sinks. Backward-compatible
// with the existing logger.js signature: logger.{level}(module, message, ctx).
//
// Phase 1.7 ships the public surface. Transport sink is still the no-op from
// Phase 1.6; Phase 1.8 swaps in the real NewRelic HTTP sink.

import { logContext, correlationScope } from './logContext';
import { logEnv, LogLevelName } from './env';
import { sanitize } from './sanitize';
import { shipBatch, LogRecord } from './transport';

// ---- Public types ---------------------------------------------------------

export type Level = LogLevelName;

export interface LogInput {
  /** snake_case event id, e.g. 'auth_login_submit'. Required for all new code. */
  event?: string;
  /** Optional semantic action; defaults to event. */
  action?: string;
  /** Component name (React component, hook, util). */
  component?: string;
  /** Module name; usually the feature folder. */
  module?: string;
  /** Route path. Defaults to window.location.pathname. */
  route?: string;
  /** HTTP method for api_* events. */
  method?: string;
  /** API endpoint path for api_* events. */
  endpoint?: string;
  /** HTTP status for api_* events. */
  status?: number;
  /** Elapsed ms for timed events. */
  durationMs?: number;
  /** Conditional branch documentation. */
  branch?: { name: string; result: boolean; reason?: string };
  /** Freeform sanitized payload. */
  data?: Record<string, unknown>;
  /** Error instance or plain object; serialized by sanitize. */
  error?: unknown;
  /** Override correlationId (otherwise picked up from correlationScope). */
  correlationId?: string;
}

// ---- Internal helpers -----------------------------------------------------

const LEVEL_RANK: Record<Level, number> = { debug: 10, info: 20, warn: 30, error: 40 };

function shouldEmit(level: Level): boolean {
  return LEVEL_RANK[level] >= LEVEL_RANK[logEnv.minLevel];
}

function deriveEventFromMessage(message: string): string {
  return (message || 'unknown_event')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'unknown_event';
}

function currentRoute(): string | undefined {
  if (typeof window === 'undefined' || !window.location) return undefined;
  return window.location.pathname;
}

function viewportSize(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return `${window.innerWidth}x${window.innerHeight}`;
}

function buildRecord(level: Level, moduleName: string, message: string, input: LogInput): LogRecord {
  const ctx = logContext.snapshot();
  const event = input.event ?? deriveEventFromMessage(message);
  const action = input.action ?? event;

  const rawRecord = {
    timestamp: new Date().toISOString(),
    level,
    service: 'zopeo-frontend',
    sessionId: ctx.sessionId,
    userId: ctx.userId,
    role: ctx.role,
    storeUUID: ctx.storeUUID,
    mallUUID: ctx.mallUUID,
    brandUUID: ctx.brandUUID,
    clientType: ctx.clientType,
    env: ctx.env,
    appVersion: ctx.appVersion,
    buildSha: ctx.buildSha,
    correlationId: input.correlationId ?? correlationScope.current(),
    route: input.route ?? currentRoute(),
    module: moduleName,
    component: input.component,
    action,
    event,
    message,
    method: input.method,
    endpoint: input.endpoint,
    status: input.status,
    durationMs: input.durationMs,
    branch: input.branch,
    data: input.data,
    error: input.error,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    viewport: viewportSize(),
    // Mark records that were missing an explicit event; surfaced in Phase 5.6 cleanup.
    legacyMissingEvent: input.event ? undefined : true,
  };

  return sanitize(rawRecord) as LogRecord;
}

function emit(level: Level, moduleName: string, message: string, input: LogInput): void {
  if (!shouldEmit(level)) return;
  let record: LogRecord;
  try {
    record = buildRecord(level, moduleName, message, input);
  } catch {
    // Building must never throw into caller code.
    return;
  }
  shipBatch(record);
}

// ---- Public surface -------------------------------------------------------

export interface Logger {
  debug(module: string, message: string, input?: LogInput): void;
  info(module: string, message: string, input?: LogInput): void;
  warn(module: string, message: string, input?: LogInput): void;
  error(module: string, message: string, input?: LogInput): void;
  withCorrelationId<T>(id: string, fn: () => T): T;
  startTimer(event: string, module: string): (extra?: Omit<LogInput, 'event' | 'durationMs'> & { level?: Level }) => void;
}

export const logger: Logger = Object.freeze({
  debug: (m, msg, input = {}) => emit('debug', m, msg, input),
  info: (m, msg, input = {}) => emit('info', m, msg, input),
  warn: (m, msg, input = {}) => emit('warn', m, msg, input),
  error: (m, msg, input = {}) => emit('error', m, msg, input),

  withCorrelationId<T>(id: string, fn: () => T): T {
    return correlationScope.run(id, fn);
  },

  startTimer(event: string, module: string) {
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    return (extra: Omit<LogInput, 'event' | 'durationMs'> & { level?: Level } = {}) => {
      const { level = 'info', ...rest } = extra;
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      emit(level, module, event, { ...rest, event, durationMs: Math.round(now - t0) });
    };
  },
});

export default logger;
